import type { Client } from '@hero/schema';

/** A role a `Client` can hold. Subset of `partyRoleSchema`. */
export type ClientRole = Client['roles'][number];

/**
 * Per-role configuration for the CRM role picker.
 *
 * Drives both the list of selectable roles and the exclusivity rules
 * (e.g. `individual_customer` cannot be combined with any other role).
 */
export interface ClientRoleConfig {
  id: ClientRole;
  /**
   * Whether this role can be combined with other roles in the same Client.
   *
   * For example `individual_customer` is exclusive — a single human
   * cannot also be a registered `business_customer` or `carrier_company`.
   */
  canBeSelectedWithOtherRoles: boolean;
  /**
   * When true, the role represents a single individual rather than a
   * registered business. The Client form should hide the dedicated
   * "Client Name" input and treat the primary contact's `personName`
   * as the client's display name (a person and "their company" are
   * the same entity here, so the two fields would be redundant).
   */
  usesPersonNameAsClientName?: boolean;
}

const business_customer: ClientRoleConfig = {
  id: 'business_customer',
  canBeSelectedWithOtherRoles: true,
};
const carrier_company: ClientRoleConfig = {
  id: 'carrier_company',
  canBeSelectedWithOtherRoles: true,
};
const broker: ClientRoleConfig = {
  id: 'broker',
  canBeSelectedWithOtherRoles: true,
};
const individual_customer: ClientRoleConfig = {
  id: 'individual_customer',
  canBeSelectedWithOtherRoles: false,
  usesPersonNameAsClientName: true,
};
const driver: ClientRoleConfig = {
  id: 'driver',
  canBeSelectedWithOtherRoles: false,
  usesPersonNameAsClientName: true,
};
const contact: ClientRoleConfig = {
  id: 'contact',
  canBeSelectedWithOtherRoles: false,
  usesPersonNameAsClientName: true,
};

/**
 * Roles a CRM user can pick from when adding a Client. Order here is
 * the canonical display order; selection-aware UIs should preserve it
 * when serializing back to `Client.roles`.
 */
const CRM_PUBLIC_ROLES: readonly ClientRoleConfig[] = [
  business_customer,
  carrier_company,
  broker,
  individual_customer,
];

/** Every known role across the platform (CRM-public + system). */
const ALL_ROLES: readonly ClientRoleConfig[] = [...CRM_PUBLIC_ROLES, driver, contact];

export const client = {
  /**
   * Roles available in the CRM role picker (the multi-select on the
   * Client form). Excludes system roles like `driver` / `contact`
   * which are managed elsewhere.
   */
  CRM_PUBLIC_ROLES,

  /** Every known role across the platform. */
  ALL_ROLES,

  /** Lookup a role's config by id. */
  getRoleConfig(id: ClientRole): ClientRoleConfig | undefined {
    return ALL_ROLES.find(role => role.id === id);
  },

  /**
   * Whether the given selection represents a "personal" client — i.e.
   * one whose display name is the contact person's name rather than a
   * separate company name. Returns true when there is at least one
   * role and EVERY selected role is flagged
   * `usesPersonNameAsClientName`.
   *
   * UIs use this to hide the redundant "Client Name" field on a
   * Client create/edit form and mirror `personName` into `clientName`
   * on submit.
   */
  isPersonalClient(roles: readonly ClientRole[] | undefined): boolean {
    if (!roles || roles.length === 0) return false;
    return roles.every(role => {
      const cfg = ALL_ROLES.find(r => r.id === role);
      return cfg?.usesPersonNameAsClientName === true;
    });
  },

  /**
   * Whether `role` should be disabled in the picker given the current
   * selection.
   *
   * Rule (intentionally one-way): an exclusive role is **always
   * clickable** — picking it is how the user "switches" away from a
   * combinable selection. The act of selecting an exclusive role
   * (handled by `resolveRoleSelection`) clears the previous
   * selection, and once it is selected the other roles report as
   * disabled here so the UI makes the lock-in visible.
   *
   * A role is disabled iff:
   *  1. It's currently selected — never disable an option the user
   *     can still toggle off.
   *  2. Some currently-selected role is exclusive AND it isn't
   *     `role` (the active exclusive lock prevents combining).
   *
   * Note: exclusive roles are NOT disabled when other (combinable)
   * roles are selected. Clicking them clears the others — that's the
   * intended affordance.
   */
  isRoleDisabled(role: ClientRole, currentSelection: readonly ClientRole[]): boolean {
    const selectedSet = new Set<ClientRole>(currentSelection);
    if (selectedSet.has(role)) return false;

    for (const selected of selectedSet) {
      if (selected === role) continue;
      const cfg = ALL_ROLES.find(r => r.id === selected);
      if (cfg && !cfg.canBeSelectedWithOtherRoles) return true;
    }
    return false;
  },

  /**
   * Apply the role picker's exclusivity rules to a tentative selection.
   *
   * Defensive guard for callers that don't pre-disable conflicting
   * options (`isRoleDisabled`). Rules:
   *  1. If the just-toggled role is exclusive (`canBeSelectedWithOtherRoles: false`)
   *     and is now selected, return `[role]` and drop everything else.
   *  2. If any *other* role in the selection is exclusive, drop it
   *     (the user is now picking a non-exclusive role on top of an
   *     exclusive one — non-exclusive wins).
   *  3. Otherwise return the input as-is.
   *
   * Always returned in the canonical `CRM_PUBLIC_ROLES` order.
   */
  resolveRoleSelection(
    nextSelection: readonly ClientRole[],
    toggledRole: ClientRole,
  ): ClientRole[] {
    const selectedSet = new Set<ClientRole>(nextSelection);
    if (!selectedSet.has(toggledRole)) {
      // Deselect: nothing to enforce; just return ordered.
      return CRM_PUBLIC_ROLES.filter(r => selectedSet.has(r.id)).map(r => r.id);
    }

    const toggledConfig = ALL_ROLES.find(r => r.id === toggledRole);
    if (toggledConfig && !toggledConfig.canBeSelectedWithOtherRoles) {
      return [toggledRole];
    }

    // Drop any previously-selected exclusive roles when a non-exclusive
    // role joins them.
    for (const role of selectedSet) {
      const cfg = ALL_ROLES.find(r => r.id === role);
      if (cfg && !cfg.canBeSelectedWithOtherRoles && role !== toggledRole) {
        selectedSet.delete(role);
      }
    }
    return CRM_PUBLIC_ROLES.filter(r => selectedSet.has(r.id)).map(r => r.id);
  },
};
